import React, { useState, useCallback, useRef, useEffect, useId } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { searchService } from '../../services/searchService';
import type { City } from '../../services/searchService';

interface CityAutocompleteProps {
  value: string;
  onChange: (city: string) => void;
  onCitySelect?: (city: City) => void;
  label: string;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
}

export const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  onCitySelect,
  label,
  placeholder,
  error,
  hint,
  required = false,
  disabled = false,
}) => {
  const { token } = useAuth();
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Accessibility IDs
  const inputId = useId();
  const listboxId = useId();
  const errorId = useId();
  const hintId = useId();

  // Sync input value with prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCities = useCallback(async (query: string) => {
    if (!query || query.length < 2 || !token) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchService.searchCities(token, query, 10);
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('City search error:', err);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchCities(newValue);
    }, 300);
  };

  const handleCitySelect = (city: City) => {
    setInputValue(city.Name);
    onChange(city.Name);
    setSuggestions([]);
    setShowDropdown(false);

    if (onCitySelect) {
      onCitySelect(city);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleCitySelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  const hasError = Boolean(error);
  const hasHint = Boolean(hint) && !hasError;

  // Build aria-describedby based on what's present
  const describedBy = [
    hasError ? errorId : null,
    hasHint ? hintId : null,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div className="relative">
      <label htmlFor={inputId} className="block text-sm font-medium text-ink-primary mb-1.5">
        {label}
        {required && <span className="text-brand-warm-deep ml-1" aria-hidden="true">*</span>}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-expanded={showDropdown && suggestions.length > 0}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          className={`w-full px-4 py-3 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 transition-colors ${
            error
              ? 'border-brand-warm focus:ring-red-500/50'
              : 'border-border-strong focus:ring-brand-primary/50 focus:border-brand-primary'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          autoComplete="off"
        />

        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2" aria-hidden="true">
            <svg className="animate-spin h-5 w-5 text-ink-secondary" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label={label}
          className="absolute z-50 w-full mt-1 bg-surface-app-elevated border border-border-strong rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((city, index) => (
            <button
              key={city.Id}
              id={`${listboxId}-option-${index}`}
              type="button"
              role="option"
              aria-selected={index === highlightedIndex}
              onClick={() => handleCitySelect(city)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full px-4 py-3 text-left transition-colors ${
                index === highlightedIndex
                  ? 'bg-brand-primary text-white'
                  : 'text-ink-primary hover:bg-surface-card'
              }`}
            >
              <div className="font-medium">{city.Name}</div>
              <div className="text-sm opacity-75">
                {city.Province && `woj. ${city.Province}`}
                {city.District && city.District !== city.Name && `, pow. ${city.District}`}
                {city.Type && ` (${city.Type})`}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Hint */}
      {hasHint && (
        <p id={hintId} className="mt-1.5 text-sm text-ink-muted">{hint}</p>
      )}

      {/* Error */}
      {hasError && (
        <p id={errorId} className="mt-1.5 text-sm text-brand-warm-deep" role="alert">{error}</p>
      )}
    </div>
  );
};
